import { useState, useCallback } from 'react';

export interface FeedbackData {
  type: 'bug' | 'feature' | 'improvement' | 'praise' | 'support';
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  category: string;
  userAgent: string;
  viewState: ViewState;
  userContext: UserContext;
  timestamp: string;
  userId?: string;
}

export interface ViewState {
  currentPage: string;
  currentComponent: string;
  userActions: string[];
  formData?: Record<string, any>;
  errorMessages?: string[];
  loadingStates?: string[];
}

export interface UserContext {
  userRole: string;
  userPreferences: Record<string, any>;
  recentActivity: string[];
  deviceInfo: {
    platform: string;
    browser: string;
    screenSize: string;
  };
}

export const useFeedback = () => {
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<'bug' | 'feature' | 'improvement' | 'praise' | 'support'>('feature');
  const [feedbackTitle, setFeedbackTitle] = useState('');
  const [feedbackDescription, setFeedbackDescription] = useState('');
  const [feedbackPriority, setFeedbackPriority] = useState<'low' | 'medium' | 'high' | 'critical'>('medium');
  const [feedbackCategory, setFeedbackCategory] = useState('');

  const openFeedback = useCallback((type?: 'bug' | 'feature' | 'improvement' | 'praise' | 'support') => {
    if (type) {
      setFeedbackType(type);
    }
    setIsFeedbackOpen(true);
  }, []);

  const closeFeedback = useCallback(() => {
    setIsFeedbackOpen(false);
    // Reset form
    setFeedbackTitle('');
    setFeedbackDescription('');
    setFeedbackPriority('medium');
    setFeedbackCategory('');
    setFeedbackType('feature');
  }, []);

  const submitFeedback = useCallback(async (feedbackData: FeedbackData) => {
    try {
      // Here you would typically send to your backend
      // For now, we'll log it and could send to a service like Intercom, Zendesk, etc.
      
      console.log('Feedback submitted:', feedbackData);
      
      // Example: Send to external service
      // await fetch('/api/feedback', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(feedbackData)
      // });

      // For now, simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      return { success: true };
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      return { success: false, error };
    }
  }, []);

  const getCurrentViewState = useCallback((): ViewState => {
    return {
      currentPage: window.location.pathname,
      currentComponent: getCurrentComponent(),
      userActions: getRecentUserActions(),
      formData: getCurrentFormData(),
      errorMessages: getCurrentErrors(),
      loadingStates: getCurrentLoadingStates()
    };
  }, []);

  const getCurrentUserContext = useCallback((): UserContext => {
    return {
      userRole: getUserRole(),
      userPreferences: getUserPreferences(),
      recentActivity: getRecentActivity(),
      deviceInfo: {
        platform: navigator.platform,
        browser: navigator.userAgent,
        screenSize: `${window.innerWidth}x${window.innerHeight}`
      }
    };
  }, []);

  return {
    // State
    isFeedbackOpen,
    feedbackType,
    feedbackTitle,
    feedbackDescription,
    feedbackPriority,
    feedbackCategory,
    
    // Setters
    setFeedbackType,
    setFeedbackTitle,
    setFeedbackDescription,
    setFeedbackPriority,
    setFeedbackCategory,
    
    // Actions
    openFeedback,
    closeFeedback,
    submitFeedback,
    getCurrentViewState,
    getCurrentUserContext
  };
};

// Helper functions
const getCurrentComponent = (): string => {
  const path = window.location.pathname;
  if (path.includes('/dashboard')) return 'Dashboard';
  if (path.includes('/recipes')) return 'RecipeBook';
  if (path.includes('/guru')) return 'GuruConnect';
  if (path.includes('/analytics')) return 'Analytics';
  if (path.includes('/profile')) return 'UserProfile';
  if (path.includes('/settings')) return 'Settings';
  if (path.includes('/medications')) return 'MedicationManager';
  if (path.includes('/food')) return 'FoodTracking';
  if (path.includes('/symptoms')) return 'SymptomTracker';
  if (path.includes('/conditions')) return 'ConditionManager';
  return 'Unknown';
};

const getRecentUserActions = (): string[] => {
  // This would track user actions in a more sophisticated way
  // For now, return a simple array
  return [
    `${new Date().toISOString()}: Visited ${window.location.pathname}`
  ];
};

const getCurrentFormData = (): Record<string, any> | undefined => {
  // This would capture current form data if any
  return undefined;
};

const getCurrentErrors = (): string[] | undefined => {
  // This would capture any current error messages
  return undefined;
};

const getCurrentLoadingStates = (): string[] | undefined => {
  // This would capture current loading states
  return undefined;
};

const getUserRole = (): string => {
  // This would get the actual user role from your auth system
  return 'client'; // Default to client
};

const getUserPreferences = (): Record<string, any> => {
  // This would get user preferences from your system
  return {};
};

const getRecentActivity = (): string[] => {
  // This would get recent user activity
  return [];
}; 